import type { Dayjs } from 'dayjs';
import type dayjs from 'dayjs';
import type { Duration } from 'dayjs/plugin/duration';
import cloneDeep from 'lodash/cloneDeep';

import type { CheckPasswordStatus } from '@webapp/common/lib/api';
import type { ForwardLogic } from '@webapp/common/resources/entities/question';
import type { QuestionParams } from '@webapp/common/resources/question';

import type { BundleEntity } from '@webapp/survey/src/entities/bundle';
import type { AudioMachineDefinitionRef } from '@webapp/survey/src/machine/audio-machine';
import type { BootstrapMachineActor } from '@webapp/survey/src/machine/bootstrap-machine';
import type { QuestionActorRef } from '@webapp/survey/src/machine/question-machine';
import type { QuotaActorRef, QuotaClaim } from '@webapp/survey/src/machine/quota-machine';
import type { TimerMachineActor } from '@webapp/survey/src/machine/timer-machine';

import type { LogicSubCondition } from '../entities/logic';

import type { InfoModel } from './entities/info';
import { initialInfo } from './entities/info';
import type {
    AnswerType,
    LinkParamsLogicType,
    LogicBoolFunc,
    LogicTransition,
    LogicTransitionType,
    LogicTransitionVariant,
    LogicType,
} from './survey';
import { SurveyStatus, TestCheckStatus } from './survey';

export type NumBool = 0 | 1;

export interface TokenAuthSettings {
    password: NumBool | null;
    vkAuth: NumBool | null;
}

export interface TokenData {
    private: NumBool | TokenAuthSettings | null;
    license: NumBool | null;
    rid: string | number | null;
    survey: number | null;
}

export interface PageModel {
    id: number;
    title: string;
    onebyone: boolean;
    random: boolean;
    randomFixed: boolean;
    shuffleOrder: Array<number>;
}

export const initialPage: Readonly<PageModel> = {
    id: -1,
    title: '',
    onebyone: false,
    random: false,
    randomFixed: false,
    shuffleOrder: [],
};

export interface ResidenceModel {
    id: number;
    name: string;
    level: number;
    parent: number | null;
}

export interface MaxDiffBlockItemModel {
    answerId: number;
    position: number;
    value1: string;
    textBlock: string | null;
    order: number;
    filePath: string | null;
}

export type MaxDiffBlockModel = Array<Array<MaxDiffBlockItemModel>>;

export enum ImageTextPosition {
    UNDER = 'under',
    ABOVE = 'above',
    LEFT = 'left',
}

export enum QuestionParamsModelAbType {
    TEXT = 'text',
    VIDEO = 'video',
    IMAGE = 'image',
}

export interface QuestionResponseModel {
    value: boolean | string | number | null;
    extra: boolean | string | number | null;
    comment: string | null;
}

export const initialQuestionResponse: Readonly<QuestionResponseModel> = {
    value: null,
    extra: null,
    comment: null,
};

export interface LogicParamModel {
    id: number;
    checked: boolean | null;
}

export interface QuestionLogicModel {
    id: number;
    type: LogicType;
    action: string | null;
    transition: LogicTransition | null;
    transitionType: LogicTransitionType | null;
    boolFunc: LogicBoolFunc | null;
    toQuestion: number | null;
    toSurvey: number | null;
    toSurveyUrl: string | null;
    toPage: number | null;
    toWebsite: string | null;
    completeText: string | null;
    disqualText: string | null;
    params: Array<LogicParamModel>; // [])
    toPages: Array<number>;
    toQuestions: Array<number>;
    answersToHide: Array<number>;
    linkId: number | null;
    linkValue: string | null;
    linkParamsLogicType: LinkParamsLogicType;
    conditions: Array<LogicSubCondition>;
    finishTransition?: LogicTransition;
    afterQuestionId?: number;
    finishTransitionVariant?: LogicTransitionVariant;
    isExtended?: boolean;
    minCount?: number | null;
    maxCount?: number | null;
}

export interface QuestionFileResponse {
    id: number | null;
    path: string | null;
    name: string | null;
    width: number | null;
    height: number | null;
    mimeType: string | null;
}

type AnswerArea = {
    id: number;
    x: number;
    y: number;
    width: number;
    height: number;
    isDefault: boolean;
    answerId: number;
};

export interface QuestionAnswerModel {
    id: number;
    value: Array<string>;
    type: AnswerType | null;
    forwardParent: number | null;
    exception: boolean;
    testCheckStatus: TestCheckStatus;
    file: QuestionFileResponse | null;
    answerArea?: AnswerArea;
    valid: boolean;
    answeredCount: number;
    pinned: boolean;
    excluded: boolean;
    videoLink?: string;
    textBlock?: string;
    // Вариант скрыт сервером (например, квота исчерпана: hiddenReason = 'QUOTA')
    hidden?: boolean;
    hiddenReason?: string | null;
    // Вариант скрыт именно квотой: только такие ответы можно «разскрыть»,
    // когда квота освободилась (овербукинг/истёкшие брони).
    quotaHidden?: boolean;
}

export const initialQuestionAnswer: Readonly<QuestionAnswerModel> = {
    id: -1,
    value: [],
    type: null,
    exception: false,
    testCheckStatus: TestCheckStatus.NOT_CHECKED,
    file: null,
    valid: true,
    pinned: false,
    excluded: false,
    forwardParent: null,
    answeredCount: 0,
};

export interface QuestionGroupModel {
    id: number;
    name: string | null;
    type: number | null;
    numericValue: number | null;
    exception: boolean;
    pinned: boolean;
    answers: Array<string>;
}

export const initialQuestionGroup: Readonly<QuestionGroupModel> = {
    id: -1,
    name: null,
    type: null,
    numericValue: null,
    exception: false,
    pinned: false,
    answers: [],
};

export interface QuestionModel {
    id: number;
    title: string;
    page: number;
    type: number;
    order?: number | null;
    filePath?: string;
    params: QuestionParams;
    answeredCount: number;
    logics: Array<QuestionLogicModel>;
    dirty: boolean;
    expired: boolean;
    errorText: string | null;
    valid: boolean; // question valid
    invalid: boolean; // answer invalid
    commentInvalid: boolean; // comment invalid
    pinned: boolean;
    timerTime: number | null; // TODO use duration
    timerId: number | null;
    isTestCheckedQuestion: boolean | TestCheckStatus;
    maxPoints: number | null;
    currentPoints: number | null;
    targetTime: Duration;
    factTime: Duration;
    shownByLogic: boolean;
    questionColumns: number;
    forwardLogic: Array<ForwardLogic>;
}

export enum BundleType {
    REDIRECT_URL = 'redirect_url',
    REDIRECT_SURVEY = 'redirect_survey',
    TEXT = 'text',
    QUESTIONS = 'questions',
}

export enum BundleFinishType {
    DISQUAL = 'disqual',
    STOP = 'stop',
    STOP_BY_TIMER = 'stopByTimer',
    TEXT = 'TEXT',
}

export interface TestScoreAnswer {
    correct: boolean;
    answered: boolean;
}
export interface TestScoreQuestion {
    maxScore: number;
    answers: Record<string, TestScoreAnswer>;
    score: number;
    answered: boolean;
}
export interface TestScore {
    maxScore: number;
    score: number;
    percent: number;
    passed: boolean;
    questions: Record<number, TestScoreQuestion>;
}

export interface SurveyStore {
    agreementText: string | null;
    showConfirmSms: 'initial' | 'shown' | 'passed';
    domain: string | null;
    token: string | null;
    tokenData: TokenData | null;
    testScore: TestScore | null;
    promocode: string;
    status: SurveyStatus;
    errorText?: string;
    questionAnswers: Record<number, string>;

    info: InfoModel;
    pages: Array<PageModel>;
    residence: Array<ResidenceModel>;
    questions: Array<QuestionActorRef>;
    bundles: Array<BundleEntity>;
    globalLogic: Array<QuestionLogicModel>;
    extendedLogic: Array<QuestionLogicModel>;
    queryParams: Record<string, string>;
    linkParams: Record<string, string>;

    startDatetime: Dayjs;
    stopDatetime: Dayjs;

    loading: {
        init: boolean;
        load: boolean;
        fetch: boolean;
        next: boolean;
        stop: boolean;
        finish: boolean;
        details: boolean;
        quotas: boolean;
    };

    withRandom: boolean;

    // state
    currentBundleIndex: number;
    factTime: Duration;
    targetTime: Duration;
    factPoints: number | null;
    individualTries: number | null;
    progress: number;
    invalidQuestionsQuantity: number;

    scrollToQuestionId: number | null;

    /* new flags */
    auth: boolean;
    timerRef: TimerMachineActor;
    endDatetime: dayjs.Dayjs | null;
    stopReason: BundleFinishType;
    // Квота опроса набралась, пока респондент проходил: сервер вернул quotaDisqual
    quotaDisqual: boolean;
    // Общая квота опроса уже набрана на старте: сервер вернул quotaFilled
    // (опрос при этом не отключается — после изменения квоты сбор продолжится)
    quotaFilled: boolean;
    pwdCheck: CheckPasswordStatus;
    audioRef: AudioMachineDefinitionRef;
    quotaRef: QuotaActorRef;
    bootstrapRef: BootstrapMachineActor | null;

    // Бронирование квот: id последнего claim-запроса и подпись уже
    // подтверждённого набора ответов по каждой странице. Пока подпись не
    // изменилась, повторный SELECT_NEXT_BUNDLE не ходит в сервис квот.
    quotaClaimRequestId: number;
    quotaClaimSignatures: Record<number, string>;
    // Подпись набора ответов, по которому сейчас идёт claim: приходит из
    // outcome, чтобы записать подтверждённую подпись даже если к моменту
    // ответа страница успела смениться.
    quotaPendingSignature: string | null;
    // Повторное бронирование при возврате в сохранённое прохождение:
    // очередь claim'ов по ранее отвеченным квотным вопросам и текущий индекс.
    resumeQuotaClaims: Array<QuotaClaim>;
    resumeQuotaIndex: number;
}

export const initialSurveyStore: Readonly<SurveyStore> = {
    agreementText: null,
    domain: null,
    token: null,
    showConfirmSms: 'initial',
    tokenData: null,
    promocode: '',
    questionAnswers: {},

    info: cloneDeep(initialInfo),
    pages: [],
    residence: [],
    questions: [],
    bundles: [],
    globalLogic: [],
    extendedLogic: [],
    linkParams: {},
    queryParams: {},

    startDatetime: null,
    stopDatetime: null,

    loading: {
        init: true,
        load: true,
        fetch: true,
        next: false,
        stop: false,
        finish: false,
        details: false,
        quotas: false,
    },

    withRandom: false,

    factTime: null,
    targetTime: null,
    factPoints: null,

    currentBundleIndex: 0,
    individualTries: null,
    scrollToQuestionId: null,

    auth: false,
    pwdCheck: null,
    timerRef: null,
    status: SurveyStatus.STATUS_JUST_START,
    endDatetime: null,
    stopReason: null,
    quotaDisqual: false,
    quotaFilled: false,
    audioRef: null,
    quotaRef: null,
    progress: 0,
    invalidQuestionsQuantity: 0,
    testScore: null,
    bootstrapRef: null,
    quotaClaimRequestId: 0,
    quotaClaimSignatures: {},
    quotaPendingSignature: null,
    resumeQuotaClaims: [],
    resumeQuotaIndex: 0,
};
